import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Sleep that rejects with an axios-style CanceledError when `signal` aborts,
// so existing catch blocks (isCancel / ERR_CANCELED checks) keep working.
function sleep(ms, signal) {
  return new Promise((resolve, reject) => {
    const t = setTimeout(resolve, ms);
    if (signal) {
      const onAbort = () => {
        clearTimeout(t);
        const e = new Error("canceled");
        e.name = "CanceledError";
        e.code = "ERR_CANCELED";
        reject(e);
      };
      if (signal.aborted) return onAbort();
      signal.addEventListener("abort", onAbort, { once: true });
    }
  });
}

/**
 * Start a long-running backend job and poll GET /api/jobs/{id} until done.
 * Exists because the production edge proxy kills any request longer than
 * ~30s while a cold news build takes 25-140s — each poll round-trip stays
 * far under that ceiling. If the start call fast-paths to a cached result
 * (`status: "done"`), no polling happens at all.
 *
 * Resolves with the job's result payload. Rejects with an axios-style error
 * (`.response.data.detail` populated) on job failure, `.code = ECONNABORTED`
 * on timeout, and a CanceledError when `signal` aborts.
 */
export async function runJob(startUrl, body, { intervalMs = 2500, timeoutMs = 180000, signal } = {}) {
  const start = await axios.post(startUrl, body, { signal, timeout: 25000 });
  if (start.data?.status === "done") return start.data.result;
  const jobId = start.data?.job_id;
  if (!jobId) throw new Error("Job did not start");
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    await sleep(intervalMs, signal);
    let r;
    try {
      r = await axios.get(`${API}/jobs/${jobId}`, { signal, timeout: 30000 });
    } catch (e) {
      // Cancellation propagates; transient poll failures (network blip,
      // slow gateway) just mean "try again on the next tick".
      if (e?.code === "ERR_CANCELED" || e?.name === "CanceledError") throw e;
      continue;
    }
    if (r.data.status === "done") return r.data.result;
    if (r.data.status === "error") {
      const err = new Error(r.data.error || "Job failed");
      err.response = { data: { detail: r.data.error }, status: r.data.status_code || 500 };
      throw err;
    }
  }
  const err = new Error("timeout: job did not finish in time");
  err.code = "ECONNABORTED";
  throw err;
}
